/**
 * Messages d'erreur lisibles par l'opérateur — UNE seule porte d'entrée.
 *
 * Règle absolue: l'écran n'affiche JAMAIS « Request failed with status code
 * 422 », « Field required », « Network Error », `detail`, un identifiant ou une
 * phrase anglaise. Toute page qui attrape une erreur écrit:
 *
 *     toast.error(messageErreur(error, 'Impossible d’enregistrer le camion'));
 *
 * et jamais `error.message` ni `error.response.data.detail`.
 *
 * Ce module ne dépend d'aucun autre module du projet que `formats.js`
 * (importé par `lib/api.js`, jamais l'inverse), donc il se teste seul.
 *
 * Ordre de recherche du message:
 *  1. `details.operator_message`: la phrase d'exploitant posée par le serveur;
 *  2. `details.rule`: règle métier → phrase de `REGLES_FR`;
 *  3. `error.code`: code stable de l'enveloppe → `CODES_FR`;
 *  4. erreurs de validation (Pydantic / FastAPI) → « Camion : champ obligatoire. »;
 *  5. le message du serveur, seulement s'il est écrit en français;
 *  6. réseau coupé / délai dépassé;
 *  7. statut HTTP (ou `repli` de la page pour 400, 404, 409, 422).
 */
import { francisaTexte } from './formats';

export const MESSAGE_PAR_DEFAUT = 'Une erreur est survenue. Réessayez.';
export const MESSAGE_RESEAU = 'Action impossible pour le moment. Réessayez dans un instant.';

/** Règles métier (`details.rule`) → phrase complète. */
export const REGLES_FR = {
  INBOUND_CONTRACT_HAS_STATEMENTS: 'Des états de paiement du transport existent pour ce contrat : il ne repasse pas au règlement mensuel. Annulez-les d’abord.',
  INBOUND_CONTRACT_SETTLED_BY_STATEMENT: 'Ce contrat est réglé par états de paiement du transport : le règlement mensuel ne s’applique pas.',
  INBOUND_CONTRACT_SETTLED_MONTHLY: 'Ce contrat est réglé au mois : passez-le en règlement par états de paiement du transport avant d’en établir un.',
  LOT_CLOSED: 'Ce lot est clos : il n’accepte plus de trajet.',
  LOT_LOADING_STOCK_FAILED: 'Le chargement de ce camion n’a pas pu être compté dans le stock.',
  LOT_NO_PRICE: 'Indiquez le prix au litre du lot avant de payer le partenaire.',
  LOT_NO_SUPPLIER: 'Le transporteur de ce lot n’a pas de compte fournisseur.',
  LOT_PRICE_LOCKED: 'Des camions de ce lot sont déjà confirmés : le prix au litre et la devise ne peuvent plus changer.',
  LOT_PRICE_REQUIRED: 'Le prix au litre du lot n’est pas encore saisi : il est recopié sur chaque trajet confirmé.',
  LOT_SETTLEMENT_CANCELLED: 'Ce décompte est annulé : il ne se valide plus.',
  LOT_SETTLEMENT_DELIVERED_KNOWN: 'Le dépoté d’un camion est déjà constaté : corrigez-le dans son dossier de manquant.',
  LOT_SETTLEMENT_NOT_CANCELLED: 'Seul un décompte annulé se supprime : annulez-le d’abord.',
  LOT_SETTLEMENT_NOT_DRAFT: 'Seul un décompte en brouillon se modifie.',
  LOT_SETTLEMENT_NOT_PAYABLE: 'Seul un décompte validé qui a encore un reste à payer se paie.',
  // Deux cas : un paiement en plus des acomptes déduits, ou l'avoir du décompte déjà imputé.
  LOT_SETTLEMENT_PAID: 'Ce décompte est déjà réglé (paiement reçu ou avoir imputé) : annulez d’abord ce règlement.',
  LOT_SETTLEMENT_TRUCK_MISSING: 'Un camion du décompte n’est plus confirmé dans le lot : retirez-le avant de valider.',
  LOT_SETTLEMENT_TRUCK_NOT_ELIGIBLE: 'Seuls les camions confirmés et dépotés de ce lot entrent sur un décompte.',
  LOT_SETTLEMENT_TRUCK_TAKEN: 'Un camion choisi figure déjà sur un autre décompte de ce lot.',
  LOT_STAGE_MISSING: 'Le parcours des trajets de la société n’a pas l’étape choisie.',
  LOT_TRANSPORTER_LOCKED: 'Des camions de ce lot sont déjà confirmés : le transporteur ne peut plus changer.',
  LOT_TRUCK_CONFIRMED: 'Ce camion est déjà confirmé : corrigez plutôt son trajet.',
  PARTNER_TRUCK_RETURNED: 'Ce camion de partenaire a été rendu à son propriétaire : attribuez-le de nouveau dans un lot avant de lui confier un trajet.',
  PRINTED_LIST_NO_RECIPIENT: 'Aucun responsable à prévenir : ajoutez le numéro WhatsApp d’un administrateur de la société dans les destinataires des alertes.',
  PRINTED_LIST_ORG_REQUIRED: 'Choisissez d’abord la société dont la liste doit partir.',
  PRINTED_LIST_TOO_LARGE: 'La liste est trop longue pour partir sur WhatsApp : affinez les filtres ou téléchargez le PDF pour l’envoyer autrement.',
  STATION_CREDIT_BLOCKED: 'Le compte crédit de ce client est bloqué.',
  STATION_CREDIT_CLIENT_REQUIRED: 'Choisissez le client de cette vente à crédit.',
  STATION_CREDIT_CLIENT_UNKNOWN: 'Ce client est inconnu.',
  STATION_CREDIT_LIMIT_EXCEEDED: 'Cette vente dépasse le plafond de crédit autorisé pour ce client.',
  STATION_CREDIT_NO_ACCOUNT: 'Ce client n’a pas de compte crédit en station.',
  STATION_CREDIT_OUTSTANDING: 'Ce compte a un encours non réglé : il ne peut pas être fermé.',
  STATION_DETAILED_SALES_MISSING: 'Les ventes détaillées de la station ne sont pas encore installées.',
  STATION_FLEET_DAILY_LIMIT: 'Le plafond du jour est dépassé pour ce véhicule.',
  STATION_FLEET_MONTHLY_LIMIT: 'Le plafond du mois est dépassé pour ce véhicule.',
  STATION_FLEET_PLATE_REQUIRED: 'Indiquez la plaque du véhicule de la flotte.',
  STATION_FLEET_PRODUCT_NOT_ALLOWED: 'Ce véhicule n’a pas le droit de prendre ce produit.',
  STATION_FLEET_VEHICLE_EXISTS: 'Ce véhicule est déjà dans cette flotte.',
  STATION_FLEET_VEHICLE_UNKNOWN: 'Ce véhicule n’est pas dans la flotte de ce client.',
  STATION_IMPORT_DUPLICATE_FILE: 'Ce relevé a déjà été importé.',
  STATION_ITEM_HAS_EMPTIES: 'Des bouteilles vides de cet article sont en stock : corrigez le stock avant de retirer la consigne.',
  STATION_ITEM_PRICE_MISSING: 'Aucun prix n’est publié pour cet article dans cette station.',
  STATION_ITEM_STOCK_INSUFFICIENT: 'Le stock de cet article est insuffisant pour cette opération.',
  STATION_MANAGEMENT_TERMS_MISSING: 'Renseignez d’abord les conditions de gérance de la station.',
  STATION_NOT_MANAGED: 'Cette station n’est pas confiée à un gérant.',
  STATION_PAYMENT_METHOD_FIXED: 'Ce moyen de paiement ne peut pas être modifié de cette manière.',
  STATION_PAYMENT_METHOD_INACTIVE: 'Ce moyen de paiement n’est pas utilisé dans cette société.',
  STATION_PRICE_CHANGE_APPLIED: 'Ce changement de prix est déjà appliqué : publiez un nouveau prix pour le corriger.',
  STATION_PRICE_CHANGE_FAILED: 'Ce changement de prix a échoué : programmez-le de nouveau.',
  STATION_RECALIBRATION_CHANGED: 'La proposition a changé : relisez-la avant de l’appliquer.',
  STATION_RECALIBRATION_NOT_READY: 'Aucune correction n’est proposée pour cette cuve.',
  STATION_STATEMENT_FUTURE: 'Ce mois n’a pas commencé : il n’y a rien à facturer.',
  STATION_STATEMENT_MONTH_OPEN: 'Le mois n’est pas terminé : le relevé se fige le mois suivant.',
  STATION_STATEMENT_NOTHING: 'Rien à facturer pour ce mois : toutes les ventes à crédit sont déjà facturées, ou il n’y en a pas.',
  STATION_VOUCHER_CLIENT_REQUIRED: 'Scannez le QR du bon, ou choisissez le client du bon.',
  STATION_VOUCHER_REQUIRED: 'Indiquez le bon carburant de cette vente.',
  STATION_VOUCHER_UNKNOWN: 'Ce bon est inconnu.',
  STATION_VOUCHER_UNUSABLE: 'Ce bon ne peut pas être utilisé pour cette vente.',
  STATION_VOUCHER_USED: 'Ce bon est déjà utilisé : il ne peut plus être annulé.',
  ALLOCATION_EXCEEDS_REMAINING_VOLUME: 'La quantité affectée dépasse le volume qui reste à livrer sur cette commande.',
  ALLOCATION_EXCEEDS_TRIP_CAPACITY: 'La quantité dépasse la capacité encore disponible sur ce trajet.',
  BREAKDOWN_DESCRIPTION_REQUIRED: 'Décrivez la panne constatée.',
  BREAKDOWN_TRUCK_REQUIRED: 'Choisissez le camion en panne.',
  COMPANY_HAS_INBOUND_CONTRACTS: 'Cette société a des contrats de location de camions: elle ne peut pas être supprimée.',
  COMPANY_HAS_ACCOUNT_ENTRIES: 'Le compte de cette société contient des opérations: elle ne peut pas être supprimée.',
  DELIVERED_ORDER_REQUIRED: 'La commande doit être livrée avant d’établir la facture.',
  DISPATCH_READINESS_BLOCKED: 'Des contrôles bloquent l’affectation: complétez ce qui manque sur la commande ou sur le trajet.',
  DUPLICATE_STATION_POS_STOCK_EFFECT: 'Cette vente a déjà été enregistrée dans le stock.',
  DUPLICATE_STOCK_BUSINESS_REFERENCE: 'Un mouvement de stock existe déjà pour cette référence.',
  FINANCE_POST_SYSTEM_MISSING: 'L’écriture financière automatique n’a pas pu être créée. Réessayez ou prévenez l’administrateur.',
  FREIGHT_AMOUNT_MISSING: 'Le montant du fret n’est pas renseigné.',
  FREIGHT_INVOICED: 'Le fret de ce trajet est déjà facturé: il ne peut plus être modifié.',
  FREIGHT_LITERS_UNKNOWN: 'Le volume transporté n’est pas connu: impossible de calculer le fret.',
  FREIGHT_NOT_BILLED: 'Ce trajet n’est pas facturé au client: il n’y a pas de fret à facturer.',
  FREIGHT_TARIFF_CURRENCY: 'Le tarif de fret de ce corridor n’est pas dans la devise de la société. Saisissez le tarif au litre ou le montant du fret.',
  FREIGHT_TARIFF_MISSING: 'Aucun tarif de fret ne correspond à ce trajet. Ajoutez-en un dans les règles tarifaires.',
  FUEL_ISSUE_EXCEEDS_APPROVAL: 'Le volume servi dépasse le volume approuvé pour cette demande.',
  FUEL_ISSUE_INVALID_QUANTITY: 'Le volume servi doit être supérieur à 0.',
  FUEL_ISSUE_TARGET_REQUIRED: 'Indiquez l’engin, le projet ou le département servi, ou une justification.',
  FUEL_REQUEST_ALREADY_DECIDED: 'Cette demande de carburant a déjà été traitée.',
  FUEL_REQUEST_NOT_APPROVED: 'La demande de carburant doit être approuvée avant la sortie.',
  IMMOBILIZATION_ALREADY_RELEASED: 'Cette immobilisation est déjà levée.',
  IMMOBILIZATION_REASON_REQUIRED: 'Indiquez le motif de l’immobilisation.',
  IMMOBILIZATION_TRUCK_REQUIRED: 'Choisissez le camion à immobiliser.',
  INBOUND_CONTRACT_CLOSED: 'Ce contrat de location est clos: il ne peut plus être modifié.',
  INBOUND_CONTRACT_DRAFT: 'Ce contrat de location est encore en brouillon: activez-le d’abord.',
  INBOUND_CONTRACT_HAS_ENTRIES: 'Ce contrat a déjà des écritures: il ne peut plus être supprimé.',
  INBOUND_CONTRACT_HAS_SETTLEMENTS: 'Ce contrat a déjà des règlements mensuels: il ne peut plus être supprimé.',
  INBOUND_CONTRACT_NOT_DRAFT: 'Seul un contrat en brouillon peut être modifié de cette façon.',
  INBOUND_SETTLEMENT_MONTH_OPEN: 'Ce mois n’est pas terminé: attendez sa fin pour établir le règlement.',
  INBOUND_TRUCK_BILLED: 'Ce camion a déjà été réglé sur ce contrat: il ne peut plus être modifié.',
  INBOUND_TRUCK_OVERLAP: 'Ce camion est déjà loué sur cette période.',
  INVOICE_CLIENT_POD_POLICY_REQUIRED: 'Le client demande une preuve de livraison: complétez-la, la facturation reste possible.',
  INVOICE_MISSION_INTEGRITY_REQUIRED: 'Une anomalie de mission est à traiter; la facturation et le paiement restent possibles.',
  INVOICE_POD_BILLING_READY_REQUIRED: 'La preuve de livraison attend un contrôle; la facturation reste possible.',
  INVOICE_RECONCILIATION_REQUIRED: 'Un écart de volume est à contrôler; la facturation et le paiement restent possibles.',
  LICENSE_MAX_TRUCKS: 'Le nombre maximal de camions de votre licence est atteint.',
  MAINTENANCE_COST_BEARER_INVALID: 'Qui paie ? Choisissez « La société » ou « Le propriétaire du camion ».',
  MISSION_INTEGRITY_JUSTIFICATION_REQUIRED: 'Un commentaire de justification est obligatoire.',
  MONTH_NOT_FINISHED: 'Ce mois n’est pas encore terminé.',
  NO_DELETE_WITH_ACTIVE_ORDERS: 'Ce client a encore des commandes en cours: impossible de le supprimer.',
  NO_DUPLICATE_INVOICES: 'Une facture existe déjà pour cette commande.',
  NO_REMAINING_QUANTITY: 'Cette commande n’a plus de volume à affecter.',
  ODOMETER_MUST_NOT_DECREASE: 'Le relevé est inférieur au dernier relevé connu du compteur.',
  ODOMETER_TRUCK_REQUIRED: 'Choisissez le camion du relevé.',
  ODOMETER_VALUE_INVALID: 'Le relevé du compteur doit être supérieur à 0.',
  ONE_ALLOCATION_PER_ORDER_TRIP: 'Ce trajet est déjà affecté à cette commande.',
  ORDER_DELETE_NOT_ALLOWED: 'Une commande facturée ou payée ne peut pas être supprimée.',
  ORDER_INTERNAL_SUPPLY_BY_RECEPTION: 'La livraison d’un réapprovisionnement se valide par la réception en station.',
  ORDER_INTERNAL_SUPPLY_NOT_INVOICED: 'Un réapprovisionnement de votre station n’a pas de facture client.',
  ORDER_SUPPLY_LOCKED: 'Cette commande a déjà des livraisons : sa nature et sa station ne changent plus.',
  ORDER_UPDATE_NOT_ALLOWED: 'Cette commande ne peut plus être modifiée dans son état actuel.',
  PLAN_DATE_INTERVAL_REQUIRED: 'Un plan au calendrier demande un intervalle en jours.',
  PLAN_MILEAGE_INTERVAL_REQUIRED: 'Un plan au kilométrage demande un intervalle en kilomètres.',
  POD_DOCUMENT_TRIP_MISMATCH: 'Ce document est rattaché à un autre trajet.',
  POSITIVE_ALLOCATION_QUANTITY: 'La quantité à affecter doit être supérieure à 0.',
  RENTAL_CONTRACT_FROZEN: 'Ce contrat de location est clos ou annulé: il ne peut plus être modifié.',
  DRIVER_CANNOT_ADVANCE_TRIP: 'Les étapes du trajet sont validées par le bureau.',
  PROFILE_IN_USE: 'Des comptes utilisent ce profil : changez-leur de profil avant de le supprimer.',
  PROFILE_LOGIN_METHOD_IN_USE: 'Des comptes utilisent ce profil : créez un autre profil pour changer de mode de connexion.',
  RENTAL_CONTRACT_NOT_ACTIVE: 'Seul un contrat en cours peut être suspendu.',
  RENTAL_NOTHING_TO_BILL: 'Rien à facturer sur cette période: tout est déjà facturé ou rien n’est validé.',
  RENTAL_PERIOD_OVERLAP: 'Ce camion est déjà loué sur cette période.',
  RENTAL_PRICING_MODE_MISMATCH: 'Les périodes ne concernent que les contrats facturés à la période.',
  RENTAL_ROTATION_INVOICED: 'Cette rotation est déjà facturée: elle ne peut plus être modifiée.',
  REPORT_READINESS_BLOCKED: 'Le rapport est bloqué: des contrôles sont à compléter.',
  STATION_ARCHIVED: 'Cette station est archivée : elle ne se modifie plus.',
  STATION_CLOSING_DIP_MISSING: 'Jaugez chaque cuve en fin de journée, après la dernière clôture de poste, avant d’arrêter la journée.',
  STATION_DAY_HAS_OPEN_SHIFT: 'Un poste est encore ouvert : clôturez-le avant d’arrêter la journée.',
  STATION_DAY_LOCKED: 'Cette journée est arrêtée : demandez à l’administrateur de la rouvrir.',
  STATION_DAY_NOT_LOCKED: 'Le gérant doit d’abord arrêter cette journée.',
  STATION_HEAD_OFFICE_VALIDATION_DISABLED: 'La validation par le siège n’est pas activée pour cette station.',
  STATION_JUSTIFICATION_REQUIRED: 'Un écart dépasse le seuil de la station : écrivez une justification.',
  STATION_NOZZLE_WITHOUT_PRODUCT: 'Un pistolet n’est relié à aucun produit : reliez-le à une cuve dans les réglages.',
  STATION_NO_NOZZLE: 'Cette station n’a aucun pistolet : ajoutez-en dans les réglages.',
  STATION_PRICE_ABOVE_CEILING: 'Ce prix dépasse le plafond fixé par l’État.',
  STATION_PRICE_CHANGE_READING_MISSING: 'Le prix a changé pendant le poste : relevez les index au changement de prix avant de clôturer.',
  STATION_PRICE_MISSING: 'Le prix de ce produit n’est pas saisi : publiez-le dans l’écran Prix.',
  STATION_PRODUCT_IN_USE: 'Ce produit est utilisé par une cuve : il ne peut pas être retiré ni changer de famille.',
  STATION_RECEPTION_ALREADY_STARTED: 'Le jaugeage avant dépotage est déjà enregistré : annulez la réception pour le refaire.',
  STATION_RECEPTION_CANCELLED: 'Cette réception est annulée.',
  STATION_RECEPTION_VALIDATED: 'Cette réception est déjà validée : corrigez le stock par un ajustement motivé.',
  STATION_SALE_CASH_REFUSED: 'Les ventes en espèces ne se saisissent pas une à une : elles sont comptées aux index à la clôture.',
  STATION_SALE_WITHOUT_SHIFT: 'Cette vente n’appartient à aucun poste.',
  STATION_SHIFT_ALREADY_OPEN: 'Un poste est déjà ouvert sur cette station.',
  STATION_SHIFT_NOT_CLOSED: 'Ce poste n’est pas clôturé.',
  STATION_SHIFT_NOT_LAST: 'Seul le dernier poste de la station peut être rouvert.',
  STATION_SHIFT_NOT_OPEN: 'Ce poste n’est pas ouvert : ouvrez un poste avant de continuer.',
  STATION_SHIFT_OPEN: 'Un poste est ouvert sur cette station : clôturez-le d’abord.',
  STATION_SHIFT_SALES_UNLINKED: 'Impossible de clôturer: des ventes ne sont pas rattachées à ce poste.',
  STATION_SUPPLY_NO_STATION: 'Ce voyage ne livre aucune cuve de station : choisissez la station et ses cuves sur la commande.',
  STATION_SUPPLY_TRIP_CANCELLED: 'Ce voyage est annulé : il ne livre plus aucune station.',
  STATION_TANK_CAPACITY_EXCEEDED: 'La livraison dépasse la capacité de la cuve.',
  STATION_TANK_HAS_NOZZLES: 'Des pistolets tirent encore de cette cuve : changez-les d’abord.',
  STATION_TANK_INACTIVE: 'Cette cuve est désactivée.',
  STATION_TANK_MOVEMENT_NOT_MANUAL: 'Les cuves de station se gèrent dans l’écran Cuves et réceptions : réception du camion ou ajustement motivé.',
  STATION_TANK_NEGATIVE: 'Le stock de la cuve deviendrait négatif.',
  STATION_TANK_NOT_EMPTY: 'Videz la cuve avant de changer son produit.',
  STATION_TANK_STOCK_REQUIRED_BEFORE_SALE: 'La cuve n’a pas assez de carburant pour cette vente.',
  STOCK_BALANCE_CANNOT_BE_NEGATIVE: 'Le stock ne peut pas devenir négatif.',
  SUFFICIENT_FUEL_FOR_ALLOCATION: 'Ce camion n’a pas assez de carburant disponible pour cette quantité.',
  SYNC_BATCH_TOO_LARGE: 'Trop d’éléments envoyés d’un coup. Réessayez dans un instant.',
  TRANSPORT_INVOICE_SERIES_USED: 'Des numéros de facture ont déjà été attribués : le numéro de départ ne change plus.',
  TRANSPORT_STATEMENT_CANCELLED: 'Cet état est annulé : il ne se valide plus.',
  TRANSPORT_STATEMENT_CHARGE_GONE: 'Une dépense déduite n’est plus à la charge du transporteur : retirez-la avant de valider.',
  TRANSPORT_STATEMENT_CHARGE_TAKEN: 'Une dépense choisie est déjà déduite sur un autre état de paiement.',
  TRANSPORT_STATEMENT_DELIVERED_KNOWN: 'Le dépoté d’un trajet est déjà constaté : corrigez-le dans son dossier de manquant.',
  TRANSPORT_STATEMENT_NOT_DRAFT: 'Seul un état en brouillon se modifie.',
  TRANSPORT_STATEMENT_PAID: 'L’avoir de cet état est déjà imputé sur une facture : il ne s’annule plus.',
  TRANSPORT_STATEMENT_SERIES_USED: 'Un état de ce transporteur est déjà numéroté : le numéro de départ ne change plus.',
  TRANSPORT_STATEMENT_TRIP_MISSING: 'Un trajet de l’état n’existe plus : retirez-le avant de valider.',
  TRANSPORT_STATEMENT_TRIP_NOT_ELIGIBLE: 'Seuls les trajets dépotés des camions de ce contrat, hors mois déjà réglés au mois, peuvent figurer sur l’état.',
  TRANSPORT_STATEMENT_TRIP_TAKEN: 'Un trajet choisi figure déjà sur un autre état de paiement.',
  TRIP_ARRIVAL_STOCK_EFFECT_REQUIRED: 'L’arrivée au dépôt ne peut pas être validée: la réception en stock n’a pas pu être enregistrée.',
  TRIP_BORDER_CROSSING_STOCK_EFFECT_REQUIRED: 'La douane d’entrée ne peut pas être validée: le solde du transit n’a pas pu être enregistré.',
  TRIP_DELETED: 'Ce trajet a été supprimé.',
  TRIP_WITHOUT_TRUCK: 'Ce trajet n’a pas de camion : la position ne peut pas être suivie.',
  POSITION_NOT_ACCEPTED: 'Ce trajet n’accepte plus de position : il est terminé ou n’est pas celui de votre société.',
  TRIP_DIRECT_DELIVERY_STOCK_EFFECT_REQUIRED: 'Le départ en livraison directe est impossible: le mouvement de stock n’a pas pu être enregistré.',
  TRIP_LOADING_STOCK_EFFECT_REQUIRED: 'Le chargement ne peut pas démarrer: le stock en transit n’a pas pu être enregistré.',
  TRIP_NOT_ASSIGNED_TO_DRIVER: 'Ce trajet ne vous est pas attribué.',
  TRIP_DEPOT_STEP_DISABLED: 'Votre société ne gère pas de dépôt : l’étape « Arrivé au dépôt » n’existe pas pour ce trajet.',
  TRIP_NOT_AVAILABLE_FOR_DISPATCH: 'Ce trajet n’est pas proposé pour cette commande.',
  TRIP_SHORTAGE_RETAINED_ON_LOT_SETTLEMENT: 'Le manquant de ce trajet est déjà retenu sur un décompte de lot : aucun avoir de plus.',
  TRIP_SHORTAGE_RETAINED_ON_STATEMENT: 'Le manquant de ce trajet est déjà retenu sur un état de paiement du transport : aucun avoir de plus.',
  TRUCK_NOT_IN_FLEET: 'Ce camion ne fait pas partie de votre flotte.',
  TRUCK_ECONOMICS_UNAVAILABLE: 'Le compte d’exploitation de ce camion n’est pas disponible pour le moment.',
  TRUCK_IMMOBILIZED: 'Ce camion est immobilisé: levez l’immobilisation avant de l’affecter.',
  TRUCK_RENTED_OUT: 'Ce camion est loué sans chauffeur: il n’est pas disponible pour un trajet.',
  TRUCK_RETURNED_TO_OWNER: 'Ce camion a été rendu à son propriétaire: il n’est plus dans votre flotte.',
  TRUCK_TECHNICAL_UNAVAILABLE: 'L’état technique de ce camion doit être rétabli avant de l’affecter.',
  UNIQUE_CODE: 'Un convoi avec ce code existe déjà.',
  UNIQUE_MATRICULE: 'Un autre camion porte déjà cette immatriculation.',
  UNIQUE_PHONE_NUMBER: 'Un client avec ce numéro de téléphone existe déjà.',
  UNIQUE_PHONE_PER_ORG: 'Un chauffeur avec ce numéro existe déjà dans votre société.',
  VALID_ORDER_STATUS_FOR_ALLOCATION: 'Cette commande ne peut plus recevoir de camion dans son état actuel.',
  VALID_STATUS_FOR_ALLOCATION: 'Ce trajet est terminé: il ne peut plus porter de commande.',
  VALID_TRIP_STATUS_FOR_ALLOCATION: 'Ce trajet est terminé: il ne peut plus porter de commande.',
  VEHICLE_DOCUMENT_COMPONENT_INVALID: 'Élément du camion inconnu (tracteur, citerne ou ensemble).',
  VEHICLE_DOCUMENT_EXPIRY_INVALID: 'La date d’expiration est illisible.',
  VEHICLE_DOCUMENT_EXPIRY_REQUIRED: 'Ce papier demande une date d’expiration.',
  VEHICLE_DOCUMENT_ISSUED_DATE_INVALID: 'La date d’émission est illisible.',
  WORKFLOW_CLIENT_POD_REQUIRED: 'Le client exige une preuve de livraison avant de poursuivre.',
  WORKFLOW_INVALID_TEMPLATE: 'Le parcours est incorrect: il faut une seule étape de départ et au moins une étape finale.',
  WORKFLOW_INVALID_TRANSITION: 'Cette étape n’est pas possible depuis l’étape actuelle du trajet.',
  WORKFLOW_REQUIREMENT_MISSING: 'Une pièce ou une information obligatoire manque pour passer à cette étape.',
  WORK_ORDER_ALREADY_CLOSED: 'Cet ordre de travail est déjà clôturé.',
  WORK_ORDER_CLOSED: 'Impossible d’ajouter une pièce à un ordre de travail clôturé.',
  WORK_ORDER_TRUCK_REQUIRED: 'Choisissez le camion de l’ordre de travail.',
};

/** Codes d'erreur stables (`error.code`) → phrase. */
export const CODES_FR = {
  AUTHENTICATION_FAILED: 'Identifiant ou mot de passe incorrect.',
  INVALID_TOKEN: 'Votre session n’est plus valable. Reconnectez-vous.',
  TOKEN_EXPIRED: 'Session expirée, reconnectez-vous.',
  FORBIDDEN: 'Vous n’avez pas le droit de faire cela.',
  RESOURCE_NOT_FOUND: 'Introuvable.',
  RESOURCE_ALREADY_EXISTS: 'Existe déjà.',
  DUPLICATE_RESOURCE: 'Existe déjà.',
  RESOURCE_CONFLICT: 'Cette opération entre en conflit avec une donnée existante.',
  VALIDATION_ERROR: 'Informations incomplètes ou incorrectes.',
  INVALID_INPUT: 'Demande incorrecte.',
  BAD_REQUEST: 'Demande incorrecte.',
  BUSINESS_RULE_VIOLATION: 'Cette opération n’est pas possible dans l’état actuel.',
  INSUFFICIENT_CREDIT: 'Le crédit disponible du client est insuffisant.',
  INSUFFICIENT_STOCK: 'Le stock disponible est insuffisant.',
  ALLOCATION_CONFLICT: 'Ce camion est déjà pris sur cette période.',
  INVALID_STATE_TRANSITION: 'Ce changement d’état n’est pas possible depuis l’état actuel.',
  DATABASE_ERROR: 'L’enregistrement a échoué. Réessayez dans un instant.',
  DATABASE_CONNECTION_ERROR: 'Service momentanément indisponible. Réessayez dans un instant.',
  RATE_LIMIT_EXCEEDED: 'Trop de tentatives, patientez un instant.',
  EXTERNAL_SERVICE_ERROR: 'Un service externe ne répond pas. Réessayez dans un instant.',
  INTERNAL_ERROR: 'Un problème est survenu. Réessayez dans un instant.',
  METHOD_NOT_ALLOWED: 'Cette action n’est pas permise ici.',
  PAYLOAD_TOO_LARGE: 'Le fichier est trop gros.',
  UNSUPPORTED_MEDIA_TYPE: 'Ce type de fichier n’est pas accepté.',
  SERVICE_UNAVAILABLE: 'Le service est momentanément indisponible. Réessayez dans un instant.',
  REQUEST_TIMEOUT: 'La réponse met trop de temps à arriver. Réessayez.',
  HTTP_ERROR: 'L’opération n’a pas pu aboutir.',
};

/** Nom d'un champ d'API → libellé lu à l'écran. */
export const CHAMPS_FR = {
  matricule: 'Immatriculation',
  tractor_plate: 'Immatriculation du tracteur',
  tank_plate: 'Immatriculation de la citerne',
  plate: 'Immatriculation',
  plate_number: 'Immatriculation',
  capacity_liters: 'Capacité (litres)',
  capacity: 'Capacité',
  truck_id: 'Camion',
  driver_id: 'Chauffeur',
  client_id: 'Client',
  trip_id: 'Trajet',
  order_id: 'Commande',
  company_id: 'Société',
  operation_id: 'Convoi',
  convoy_id: 'Convoi',
  invoice_id: 'Facture',
  station_id: 'Station',
  tank_id: 'Cuve',
  pump_id: 'Pompe',
  shift_id: 'Quart',
  site_id: 'Site',
  asset_id: 'Engin',
  project_id: 'Projet',
  department_id: 'Département',
  contract_id: 'Contrat',
  document_id: 'Document',
  organization_id: 'Société',
  user_id: 'Utilisateur',
  party_id: 'Tiers',
  account_id: 'Compte',
  target_account_id: 'Compte de destination',
  fuel_request_id: 'Demande de carburant',
  created_by_org_id: 'Société',
  quantity: 'Quantité',
  quantity_liters: 'Volume (litres)',
  quantity_requested: 'Quantité demandée',
  requested_quantity: 'Quantité demandée',
  approved_quantity: 'Quantité approuvée',
  delivered_quantity: 'Quantité livrée',
  fuel_quantity_real: 'Volume chargé',
  fuel_quantity_planned: 'Volume prévu',
  volume: 'Volume',
  unit_price: 'Prix unitaire',
  unit_cost: 'Coût unitaire',
  total_amount: 'Montant total',
  amount: 'Montant',
  price: 'Prix',
  rate: 'Tarif',
  rate_amount: 'Tarif',
  currency: 'Devise',
  date: 'Date',
  start_date: 'Date de début',
  end_date: 'Date de fin',
  due_date: 'Date de suivi',
  occurred_at: 'Date',
  issue_date: 'Date de sortie',
  delivery_date: 'Date de livraison',
  loading_date: 'Date de chargement',
  departure_date: 'Date de départ',
  expected_return_date: 'Date de retour prévue',
  expiry_date: 'Date d’expiration',
  issued_date: 'Date d’émission',
  completed_at: 'Date de clôture',
  name: 'Nom',
  first_name: 'Prénom',
  last_name: 'Nom de famille',
  full_name: 'Nom complet',
  phone: 'Téléphone',
  email: 'Adresse e-mail',
  password: 'Mot de passe',
  current_password: 'Mot de passe actuel',
  new_password: 'Nouveau mot de passe',
  address: 'Adresse',
  city: 'Ville',
  country: 'Pays',
  region: 'Région',
  latitude: 'Latitude',
  longitude: 'Longitude',
  radius_meters: 'Rayon (mètres)',
  description: 'Description',
  comment: 'Commentaire',
  notes: 'Notes',
  note: 'Note',
  text: 'Texte',
  reason: 'Motif',
  status: 'État',
  type: 'Type',
  kind: 'Type',
  category: 'Catégorie',
  role: 'Rôle',
  code: 'Code',
  reference: 'Référence',
  reference_number: 'Numéro de référence',
  contract_number: 'Numéro de contrat',
  product: 'Produit',
  product_type: 'Produit',
  destination: 'Destination',
  origin: 'Origine',
  license_number: 'Numéro de permis',
  mileage: 'Kilométrage',
  odometer: 'Compteur',
  value: 'Valeur',
  title: 'Titre',
  message: 'Message',
  label: 'Libellé',
  payment_method: 'Mode de paiement',
  payment_terms: 'Conditions de paiement',
  credit_limit: 'Plafond de crédit',
  monthly_amount: 'Montant mensuel',
  cost_bearer: 'Qui supporte le frais',
  freight_amount: 'Montant du fret',
  freight_rate_per_liter: 'Tarif du fret par litre',
  modules: 'Modules',
  plan: 'Formule',
  max_users: 'Nombre maximal d’utilisateurs',
  max_trucks: 'Nombre maximal de camions',
  access_code: 'Code d’accès',
  code_lier: 'Code de liaison',
  token: 'Jeton',
  file: 'Fichier',
  file_name: 'Nom du fichier',
  doc_type: 'Type de document',
  expires_at: 'Date d’expiration',
  opened_by: 'Ouvert par',
  closed_by: 'Clôturé par',
  movement_type: 'Type de mouvement',
  movement_date: 'Date du mouvement',
  event_type: 'Type d’événement',
  phase: 'Phase',
  blockage_reason: 'Motif de blocage',
  truck_release_phase: 'Phase de libération du camion',
  items: 'Éléments',
  body: 'Données envoyées',
  limit: 'Nombre d’éléments',
  skip: 'Décalage',
  page: 'Page',
  page_size: 'Taille de page',
};

/** Statut HTTP → phrase. */
export const STATUTS_FR = {
  400: 'Demande incorrecte.',
  401: 'Votre session a expiré. Reconnectez-vous.',
  403: 'Vous n’avez pas le droit de faire cela.',
  404: 'Élément introuvable.',
  405: 'Cette action n’est pas permise ici.',
  408: 'La réponse met trop de temps à arriver. Réessayez.',
  409: 'Cette opération entre en conflit avec une donnée existante.',
  413: 'Le fichier est trop gros.',
  415: 'Ce type de fichier n’est pas accepté.',
  422: 'Informations incomplètes ou incorrectes.',
  429: 'Trop de tentatives. Patientez un instant, puis réessayez.',
  500: 'Un problème est survenu. Réessayez dans un instant.',
  502: 'Le service ne répond pas. Réessayez dans un instant.',
  503: 'Le service est momentanément indisponible. Réessayez dans un instant.',
  504: 'La réponse met trop de temps à arriver. Réessayez.',
};

// Pour ces statuts, la page sait mieux que le serveur ce qui a échoué
// (« Impossible d’enregistrer le camion »): son `repli` passe avant la phrase du statut.
const STATUTS_OU_LA_PAGE_PARLE = new Set([400, 404, 409, 422]);

// --- Détection du français --------------------------------------------------------------

const ACCENTS = /[àâäçéèêëîïôöùûüœÀÂÇÉÈÊËÎÏÔÙÛÜŒ’]/;
const MOTS_FRANCAIS =
  /\b(le|la|les|un|une|des|du|de|d'|l'|pour|est|sont|ne|pas|doit|dans|sur|avec|ce|cette|ces|ou|et|vous|votre|aucun|aucune|déjà|impossible|introuvable|requis|requise|obligatoire|invalide|incorrect|incorrecte|existe|camion|trajet|chauffeur|client|commande|facture|montant|volume|date)\b/i;
const MOTS_ANGLAIS =
  /\b(the|is|are|was|were|not|must|cannot|failed|required|invalid|already|exists?|found|be|to|for|with|this|has|have|only|unknown|denied|missing|expired|token|permission|permissions|access|user|account|organization|exceeds?|error|unable|please|try|again|later|wrong|request|status|code|field|validation|network|timeout|detail|input|should|value)\b/i;

/** Vrai si la phrase est écrite en français (jamais une phrase anglaise du serveur). */
export const estFrancais = (texte) => {
  if (typeof texte !== 'string') return false;
  const t = texte.trim();
  if (!t) return false;
  if (ACCENTS.test(t)) return !/\b(request failed|field required|network error)\b/i.test(t);
  if (MOTS_ANGLAIS.test(t)) return false;
  return MOTS_FRANCAIS.test(t);
};

const UUID_PARTOUT = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

/** Retire les identifiants et remet la phrase au propre. */
const nettoyer = (texte) => {
  let propre = francisaTexte(String(texte));
  if (UUID_PARTOUT.test(propre)) {
    UUID_PARTOUT.lastIndex = 0;
    propre = propre.replace(UUID_PARTOUT, '').replace(/\s*\(\s*\)/g, '').replace(/\s{2,}/g, ' ').replace(/\s+([.,;])/g, '$1');
  }
  UUID_PARTOUT.lastIndex = 0;
  return propre.trim();
};

// --- Validation (Pydantic / FastAPI) -----------------------------------------------------------

const nombreFr = (valeur) => {
  const n = Number(valeur);
  return Number.isFinite(n)
    ? n.toLocaleString('fr-FR', { maximumFractionDigits: 6 })
    : String(valeur);
};

/** Complément de phrase pour un type d'erreur de validation: « champ obligatoire »… */
export const phraseValidation = (type, ctx = {}, msg = '') => {
  const t = String(type || '');
  const c = ctx || {};
  if (t === 'missing' || t.startsWith('missing_')) return 'champ obligatoire';
  if (t === 'string_type' || t === 'bytes_type') return 'doit être un texte';
  if (t === 'string_too_short') return `trop court (au moins ${nombreFr(c.min_length ?? 1)} caractère(s))`;
  if (t === 'string_too_long') return `trop long (${nombreFr(c.max_length ?? '')} caractères au plus)`;
  if (t === 'string_pattern_mismatch' || t === 'string_unicode') return 'format incorrect';
  if (/^(int_|float_|decimal_|finite_number)/.test(t)) return 'doit être un nombre';
  if (t === 'bool_type' || t === 'bool_parsing') return 'doit être oui ou non';
  if (t === 'greater_than') return `doit être supérieur à ${nombreFr(c.gt)}`;
  if (t === 'greater_than_equal') return `doit être au moins ${nombreFr(c.ge)}`;
  if (t === 'less_than') return `doit être inférieur à ${nombreFr(c.lt)}`;
  if (t === 'less_than_equal') return `ne peut pas dépasser ${nombreFr(c.le)}`;
  if (t === 'multiple_of') return `doit être un multiple de ${nombreFr(c.multiple_of)}`;
  if (t === 'enum' || t === 'literal_error' || t.startsWith('union_tag')) return 'valeur non reconnue';
  if (t.startsWith('date_')) return 'date invalide';
  if (t.startsWith('datetime_')) return 'date et heure invalides';
  if (t.startsWith('time_')) return 'heure invalide';
  if (t === 'json_invalid' || t === 'json_type') return 'données illisibles';
  if (['list_type', 'tuple_type', 'set_type', 'frozen_set_type'].includes(t)) return 'doit être une liste';
  if (t === 'too_short') return `au moins ${nombreFr(c.min_length ?? 1)} élément(s) attendu(s)`;
  if (t === 'too_long') return `${nombreFr(c.max_length ?? '')} élément(s) au plus`;
  if (['dict_type', 'model_type', 'model_attributes_type', 'model_fields_type'].includes(t)) return 'format incorrect';
  if (t === 'extra_forbidden') return 'champ non attendu';
  if (t.startsWith('uuid_')) return 'identifiant incorrect';
  if (t.startsWith('url_')) return 'adresse web incorrecte';
  if (t === 'value_error' || t === 'assertion_error') {
    const brut = msg.includes(',') ? msg.split(',').slice(1).join(',').trim() : msg;
    if (brut && estFrancais(brut)) return nettoyer(brut).replace(/\.$/, '');
  }
  return 'valeur incorrecte';
};

/** Libellé français d'un champ d'API (dernier segment d'un chemin). */
export const libelleChamp = (champ) => {
  const cle = String(champ || '').trim();
  if (!cle) return '';
  if (CHAMPS_FR[cle]) return CHAMPS_FR[cle];
  const dernier = cle.split('.').pop();
  if (CHAMPS_FR[dernier]) return CHAMPS_FR[dernier];
  if (/^\d+$/.test(dernier)) return 'Ligne';
  return 'Champ';
};

const cheminChamp = (loc) => {
  const morceaux = (Array.isArray(loc) ? loc : [loc]).filter((x) => x !== undefined && x !== null).map(String);
  if (['body', 'query', 'path', 'header', 'cookie'].includes(morceaux[0])) morceaux.shift();
  return morceaux.join('.');
};

/** Une erreur de validation → « Camion : champ obligatoire. » */
const phraseErreurValidation = (item) => {
  if (!item || typeof item !== 'object') return null;
  // Le serveur écrit déjà la phrase française: on la garde.
  if (typeof item.message === 'string' && estFrancais(item.message)) return nettoyer(item.message);
  const champ = item.field !== undefined ? item.field : cheminChamp(item.loc);
  const etiquette = libelleChamp(champ);
  const phrase = phraseValidation(item.type, item.ctx, item.msg || item.message || '');
  if (!etiquette) return item.type === 'missing' ? 'Informations incomplètes.' : `${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}.`;
  return `${etiquette} : ${phrase}.`;
};

/** Plusieurs erreurs de validation → deux phrases puis « + N autres ». */
export const resumeValidation = (liste) => {
  const phrases = [];
  (liste || []).forEach((item) => {
    const p = phraseErreurValidation(item);
    if (p && !phrases.includes(p)) phrases.push(p);
  });
  if (!phrases.length) return null;
  const reste = phrases.length - 2;
  return `${phrases.slice(0, 2).join(' ')}${reste > 0 ? ` + ${reste} autre${reste > 1 ? 's' : ''}.` : ''}`;
};

// --- Lecture d'une erreur axios -----------------------------------------------------------------

/** Enveloppe d'erreur du serveur: `{ code, message, details }`, quelle que soit sa forme. */
export const enveloppeErreur = (error) => {
  const data = error?.response?.data ?? error?.data ?? null;
  if (!data || typeof data !== 'object') return { code: null, message: null, details: null, detail: null };
  const interne = data.error && typeof data.error === 'object' ? data.error : {};
  return {
    code: interne.code || data.code || null,
    message: interne.message || data.message || null,
    details: interne.details || data.details || null,
    detail: data.detail ?? null,
  };
};

const statutDe = (error) => error?.response?.status ?? error?.status ?? null;

const estCoupureReseau = (error) => {
  if (!error || typeof error !== 'object') return false;
  if (error.response) return false;
  const code = String(error.code || '');
  return (
    code === 'ERR_NETWORK' ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    code === 'ERR_CANCELED' ||
    /network error|timeout|failed to fetch|load failed/i.test(String(error.message || '')) ||
    Boolean(error.request)
  );
};

/**
 * Phrase française d'une erreur, quelle que soit sa forme (axios, `fetch`,
 * `Error`, texte, enveloppe du serveur). Ne renvoie jamais `error.message`
 * brut, ni un identifiant, ni de l'anglais.
 *
 * @param {*} error  l'erreur attrapée
 * @param {string} [repli]  phrase de la page («  Impossible d’enregistrer le camion  »)
 */
export const messageErreur = (error, repli) => {
  const phraseRepli = repli && estFrancais(repli) ? repli : repli ? nettoyer(repli) : null;
  const final = (precis) => precis || phraseRepli || MESSAGE_PAR_DEFAUT;

  if (error === null || error === undefined) return final(null);
  if (typeof error === 'string') return estFrancais(error) ? nettoyer(error) : final(null);

  const { code, message, details, detail } = enveloppeErreur(error);

  // 1. La phrase d'exploitant posée par le serveur.
  if (details && typeof details.operator_message === 'string' && details.operator_message.trim()) {
    return nettoyer(details.operator_message);
  }
  // 2. La règle métier.
  const regle = details?.rule || (detail && typeof detail === 'object' && !Array.isArray(detail) ? detail.rule : null);
  if (regle && REGLES_FR[regle]) return REGLES_FR[regle];
  // 3. Le code stable de l'enveloppe (sauf VALIDATION_ERROR, traité plus bas).
  const codeUtile = code && code !== 'VALIDATION_ERROR' && code !== 'BUSINESS_RULE_VIOLATION' ? code : null;
  // 4. Validation.
  const listeValidation = Array.isArray(details?.errors)
    ? details.errors
    : Array.isArray(detail)
      ? detail
      : Array.isArray(error?.response?.data?.errors)
        ? error.response.data.errors
        : null;
  if (listeValidation && listeValidation.length) {
    const resume = resumeValidation(listeValidation);
    if (resume) return resume;
  }
  // 5. Le message du serveur, s'il est en français (il prime sur le code générique).
  const textes = [
    message,
    typeof detail === 'string' ? detail : null,
    detail && typeof detail === 'object' && !Array.isArray(detail) ? detail.message : null,
  ];
  for (const texte of textes) {
    if (typeof texte === 'string' && estFrancais(texte)) return nettoyer(texte);
  }
  if (codeUtile && CODES_FR[codeUtile]) return CODES_FR[codeUtile];
  if (code === 'VALIDATION_ERROR') return final(STATUTS_FR[422]);
  // 6. Réseau.
  if (estCoupureReseau(error)) return MESSAGE_RESEAU;
  // 7. Statut HTTP.
  const statut = statutDe(error);
  if (statut) {
    if (STATUTS_OU_LA_PAGE_PARLE.has(statut) && phraseRepli) return phraseRepli;
    if (STATUTS_FR[statut]) return STATUTS_FR[statut];
    if (statut >= 500) return STATUTS_FR[500];
    if (statut >= 400) return final(STATUTS_FR[400]);
  }
  return final(null);
};
